
import { keyboardMap, hasLetterSwitchingRisk, hasConsonantClusters, countSyllables, isPronounceableDomain } from './keyboardUtils';

export interface ScoreBreakdown {
  fingerEffort: number;
  handAlternation: number;
  repetitionComplexity: number;
  lengthSimplicity: number;
  phoneticClarity: number;
  total: number;
}

export function calculateTypabilityScore(domain: string): ScoreBreakdown {
  // Remove TLD and normalize
  const normalizedDomain = domain.toLowerCase().split('.')[0];
  
  // 1. Finger Effort & Key Position (20 points)
  const fingerEffortScore = calculateFingerEffortScore(normalizedDomain);
  
  // 2. Hand Alternation & Flow (20 points)
  const handAlternationScore = calculateHandAlternationScore(normalizedDomain);
  
  // 3. Repetition & Pattern Complexity (20 points)
  const repetitionComplexityScore = calculateRepetitionComplexityScore(normalizedDomain);
  
  // 4. Length & Simplicity (20 points)
  const lengthSimplicityScore = calculateLengthSimplicityScore(normalizedDomain);
  
  // 5. Phonetic Clarity (20 points)
  const phoneticClarityScore = calculatePhoneticClarityScore(normalizedDomain);
  
  // Calculate total score (0-100)
  const totalScore = Math.round(
    fingerEffortScore + handAlternationScore + repetitionComplexityScore + 
    lengthSimplicityScore + phoneticClarityScore
  );
  
  return {
    fingerEffort: Math.round(fingerEffortScore),
    handAlternation: Math.round(handAlternationScore),
    repetitionComplexity: Math.round(repetitionComplexityScore),
    lengthSimplicity: Math.round(lengthSimplicityScore),
    phoneticClarity: Math.round(phoneticClarityScore),
    total: Math.min(100, Math.max(0, totalScore)) // Ensure score is between 0-100
  };
}

// 1. Finger Effort & Key Position (20 points)
function calculateFingerEffortScore(domain: string): number {
  let score = 20; // Start with max score
  
  // Count pinky, ring finger usage, and row distribution
  let pinkyCount = 0;
  let ringCount = 0;
  let homeRowCount = 0;
  let topBottomRowCount = 0;
  
  for (const char of domain) {
    const keyInfo = keyboardMap[char.toLowerCase()];
    
    if (!keyInfo) continue; // Skip if character not on keyboard map
    
    // Pinky usage (-0.5 per use, max -5)
    if (keyInfo.finger === 'pinky') {
      pinkyCount++;
    }
    
    // Ring finger usage (-0.25 per use, max -2.5)
    if (keyInfo.finger === 'ring') {
      ringCount++;
    }
    
    // Home row usage (+0.5 per use, max +5)
    if (keyInfo.row === 'home') {
      homeRowCount++;
    } 
    // Top/bottom row penalty (-0.25 per use, max -2.5)
    else {
      topBottomRowCount++;
    }
  }
  
  // Apply penalties and bonuses
  score -= Math.min(5, pinkyCount * 0.5); // Pinky penalty
  score -= Math.min(2.5, ringCount * 0.25); // Ring finger penalty
  score += Math.min(5, homeRowCount * 0.5); // Home row bonus
  score -= Math.min(2.5, topBottomRowCount * 0.25); // Top/bottom row penalty
  
  return Math.max(0, Math.min(20, score));
}

// 2. Hand Alternation & Flow (20 points)
function calculateHandAlternationScore(domain: string): number {
  let score = 20; // Start with max score
  
  let previousHand: string | null = null;
  let alternationCount = 0;
  let sameHandClusterCount = 0;
  let previousChar: string | null = null;
  const fingerUsageCount: {[key: string]: number} = {};
  
  for (const char of domain) {
    const keyInfo = keyboardMap[char.toLowerCase()];
    
    if (!keyInfo) continue; // Skip if character not on keyboard map
    
    // Count finger usage
    const fingerKey = `${keyInfo.hand}-${keyInfo.finger}`;
    fingerUsageCount[fingerKey] = (fingerUsageCount[fingerKey] || 0) + 1;
    
    // Check hand alternation
    if (previousHand && keyInfo.hand !== previousHand) {
      alternationCount++;
    }
    
    // Check for same-hand clusters (3+ consecutive keys on same hand)
    if (previousHand && keyInfo.hand === previousHand) {
      sameHandClusterCount++;
    } else {
      sameHandClusterCount = 0;
    }
    
    previousHand = keyInfo.hand;
    previousChar = char.toLowerCase();
  }
  
  // Apply bonuses and penalties
  
  // Alternation bonus
  const alternationPercentage = alternationCount / (domain.length - 1);
  score += Math.min(5, alternationPercentage * 10); // Up to +5 bonus
  
  // Same-hand cluster penalty
  score -= Math.min(5, sameHandClusterCount); // Up to -5 penalty
  
  // Repetitive finger use penalty
  let repetitiveFingerPenalty = 0;
  Object.values(fingerUsageCount).forEach(count => {
    if (count > 2) {
      repetitiveFingerPenalty += (count - 2) * 0.5; // -0.5 for each use beyond 2
    }
  });
  score -= Math.min(5, repetitiveFingerPenalty); // Cap at -5
  
  return Math.max(0, Math.min(20, score));
}

// 3. Repetition & Pattern Complexity (20 points)
function calculateRepetitionComplexityScore(domain: string): number {
  let score = 20; // Start with max score
  
  // Check for double letters
  let doubleLetterCount = 0;
  for (let i = 0; i < domain.length - 1; i++) {
    if (domain[i] === domain[i + 1]) {
      doubleLetterCount++;
    }
  }
  
  // Letter switching risk (like googel vs google)
  const switchRiskCount = hasLetterSwitchingRisk(domain);
  
  // Mirror patterns (bonus)
  let mirrorPatternsCount = 0;
  const patternLength = 4; // Look for 4-letter mirror patterns
  for (let i = 0; i <= domain.length - patternLength; i++) {
    const segment = domain.substring(i, i + patternLength);
    if (segment === segment.split('').reverse().join('')) {
      mirrorPatternsCount++;
    }
  }
  
  // Apply penalties and bonuses
  score -= Math.min(5, doubleLetterCount * 1); // Double letter penalty
  score -= Math.min(7.5, switchRiskCount * 2.5); // Letter switching risk (more severe)
  score += Math.min(2.5, mirrorPatternsCount * 1.25); // Mirror pattern bonus
  
  return Math.max(0, Math.min(20, score));
}

// 4. Length & Simplicity (20 points)
function calculateLengthSimplicityScore(domain: string): number {
  let score = 20; // Start with max score
  
  // Character count (shorter is better)
  const charCount = domain.length;
  if (charCount <= 5) {
    score += 5; // Bonus for very short domains
  } else if (charCount <= 8) {
    score += 2.5; // Smaller bonus for medium-length domains
  } else if (charCount > 12) {
    score -= Math.min(7.5, (charCount - 12) * 1.5); // Penalty for long domains
  }
  
  // Syllable count (fewer is better)
  const syllableCount = countSyllables(domain);
  if (syllableCount <= 2) {
    score += 2.5; // Bonus for 1-2 syllables
  } else if (syllableCount > 3) {
    score -= Math.min(5, (syllableCount - 3) * 1.25); // Penalty for many syllables
  }
  
  // Mixed characters (digits, hyphens, etc.)
  const nonAlphabeticCount = domain.replace(/[a-z]/gi, '').length;
  score -= Math.min(5, nonAlphabeticCount * 1); // Penalty for mixed characters
  
  // Consonant clusters (hard to pronounce)
  const consonantClusterCount = hasConsonantClusters(domain);
  score -= Math.min(5, consonantClusterCount * 2.5); // Penalty for consonant clusters
  
  return Math.max(0, Math.min(20, score));
}

// 5. Phonetic Clarity (20 points)
function calculatePhoneticClarityScore(domain: string): number {
  let score = 20; // Start with max score
  
  // Pronounceable word bonus
  if (isPronounceableDomain(domain)) {
    score += 5;
  } else {
    score -= 7.5;
  }
  
  // Ambiguity in pronunciation
  // Check for ambiguous character combinations
  const ambiguousCombinations = [
    'ph', 'gh', 'kn', 'gn', 'pn', 'ps', 'mn', 'xh', 'zh', 'qi', 'xi', 'cz', 'xz', 'wr'
  ];
  
  let ambiguityCount = 0;
  for (const combo of ambiguousCombinations) {
    if (domain.includes(combo)) {
      ambiguityCount++;
    }
  }
  
  score -= Math.min(5, ambiguityCount * 2.5); // Penalty for ambiguous pronunciation
  
  // Check for unusual character combinations
  const unusualCombinations = domain.match(/[bcdfghjklmnpqrstvwxz]{3,}/g) || [];
  score -= Math.min(5, unusualCombinations.length * 2.5);
  
  return Math.max(0, Math.min(20, score));
}

// Get the score tier and emoji
export function getScoreTier(score: number): { tier: string; emoji: string } {
  if (score >= 85) return { tier: 'Typing Heaven', emoji: '🔥' };
  if (score >= 70) return { tier: 'Smooth', emoji: '✅' };
  if (score >= 50) return { tier: 'Watch Out', emoji: '⚠️' };
  return { tier: 'Typing Hell', emoji: '❌' };
}
